import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { DateUtils } from 'react-day-picker';
import DayPickerInput from 'react-day-picker/DayPickerInput';
import 'react-day-picker/lib/style.css';
import * as util from '../../utils/urlParams';
import { DiagnosticsActions, UtilityActions } from '../../store';
import './DatePicker.css';

export class DatePicker extends Component {
  state = {
    from: undefined,
    to: undefined,
  };

  componentDidMount() {
    this.dayPickerInputRef.current.getInput().focus();
    const inputNode = this.dayPickerInputRef.current.getInput();

    // add event listener for when input field loses focus & compare DOM nodes
    // to determine if the onClose function should be called
    this.dayPickerInputRef.current.getInput().addEventListener('blur', () => {
      setTimeout(() => {
        const activeEl = document.activeElement;
        if (document.hasFocus() && activeEl.isEqualNode(inputNode)) {
          return;
        }
        this.props.onClose({});
      });
    });
  }

  dayPickerInputRef = React.createRef();

  handleDayClick = (day, modifiers = {}) => {
    if (modifiers.disabled) {
      return;
    }
    const range = DateUtils.addDayToRange(day, this.state);
    this.setState(
      (state) => ({
        ...state,
        ...range,
      }),
      async () => {
        const { from, to } = this.state;
        if (!from || !to || !this.props.onClose) {
          return;
        }

        this.redirect(from, to);
        this.props.onClose();
      },
    );
  };

  redirect(startDatetime, endDatetime) {
    const {
      location,
      customDataPoints,
      updateDiagnosticsGraphDateRange,
      updateElectricalGraphDateRange,
      updateGasGraphDateRange,
      navigationParams: {
        selectedCategory: { name: categoryName },
        selectedSubCategory,
      },
    } = this.props;
    const { search } = location;
    const graphDateRange = {
      value: 'custom',
      label: 'Select Range',
      startDatetime,
      endDatetime,
    };

    if (categoryName === 'Diagnostics') {
      // TO DO: Investigate why this behaves differently than thermostats and energyuse
      updateDiagnosticsGraphDateRange(graphDateRange, customDataPoints);
    } else if (categoryName === 'Electrical Usage') {
      updateElectricalGraphDateRange(graphDateRange);
    } else {
      updateGasGraphDateRange(graphDateRange);
    }

    const thermostat = util.getThermostatFromUrlParams(search);
    let newSearch = '';
    if (thermostat) {
      const selectedDataPoints = util.getDataPointsFromUrlParams(search);
      newSearch = util.addThermostatSearchParams(graphDateRange, thermostat, selectedDataPoints);
    } else {
      let customDataPoints = JSON.parse(window.sessionStorage.getItem('customDataPoints')) || {};

      const customDataPointsForSubCategory = customDataPoints[selectedSubCategory.id] || [];

      newSearch = util.addSearchParams(graphDateRange, customDataPointsForSubCategory);
    }

    if (search.indexOf(newSearch) !== -1) {
      return null;
    }

    this.props.history.push(`${location.pathname}?${newSearch}`);
  }

  render() {
    const { from, to } = this.state;
    const modifiers = { start: from, end: to };
    const today = moment().toDate();
    const {
      selectedBuilding: { installation_start_date, savings_start_datetime },
      navigationParams: {
        selectedCategory: { name: categoryName },
      },
    } = this.props;

    let projectStartDate;
    if (categoryName === 'Diagnostics') {
      projectStartDate = installation_start_date;
    } else {
      projectStartDate = savings_start_datetime;
    }
    const beforeProjectStart = moment(projectStartDate).startOf('day').toDate();

    return (
      <React.Fragment>
        <DayPickerInput
          inputProps={{
            readOnly: true,
            type: 'button',
          }}
          dayPickerProps={{
            className: 'Selectable',
            disabledDays: {
              before: beforeProjectStart,
              after: today,
            },
            initialMonth: moment().subtract(1, 'months').toDate(),
            modifiers,
            numberOfMonths: 2,
            onDayClick: this.handleDayClick,
            selectedDays: [from, { from, to }],
            fromMonth: beforeProjectStart,
            toMonth: today,
          }}
          hideOnDayClick={false}
          ref={this.dayPickerInputRef}
        />
      </React.Fragment>
    );
  }
}

DatePicker.propTypes = {
  customDataPoints: PropTypes.arrayOf(PropTypes.string),
  /* eslint-disable react/forbid-prop-types */
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  /* eslint-enable react/forbid-prop-types */
  onClose: PropTypes.func.isRequired,
  navigationParams: PropTypes.shape({
    selectedCategory: PropTypes.shape({
      api_location: PropTypes.string,
      url: PropTypes.string,
      name: PropTypes.string,
      image: PropTypes.string,
      sub_categories: PropTypes.arrayOf(
        PropTypes.shape({
          api_location: PropTypes.string,
          data_api_location: PropTypes.string,
          id: PropTypes.string,
          url: PropTypes.string,
          name: PropTypes.string,
        }),
      ),
    }),
    selectedSubCategory: PropTypes.shape({
      api_location: PropTypes.string,
      data_api_location: PropTypes.string,
      id: PropTypes.string,
      url: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
};

DatePicker.defaultProps = {
  customDataPoints: [],
  navigationParams: {
    selectedCategory: {},
    selectedSubCategory: {},
  },
};

function mapStateToProps(state) {
  return {
    customDataPoints: state.diagnosticsReducer.customDataPointsList,
    navigationParams: state.buildingReducer.navigationParams,
    selectedBuilding: state.buildingReducer.selectedBuilding,
  };
}

const mapDispatchToProps = {
  updateDiagnosticsGraphDateRange: DiagnosticsActions.updateDiagnosticsGraphDateRange,
  updateElectricalGraphDateRange: UtilityActions.updateElectricalGraphDateRange,
  updateGasGraphDateRange: UtilityActions.updateGasGraphDateRange,
};

const ConnectedDatePicker = connect(mapStateToProps, mapDispatchToProps)(DatePicker);
const ConnectedDatePickerWithRouter = withRouter(ConnectedDatePicker);

export { ConnectedDatePickerWithRouter };
